import { useMemo } from 'react';
import DataEditor, { GridCell, GridCellKind, GridColumn, Item } from '@glideapps/glide-data-grid';
import { Box, Text } from '@chakra-ui/react';

import { accent, font, neutral, radius, text } from 'styles/revampTokens';

import '@glideapps/glide-data-grid/dist/index.css';

export interface TaskGridCell {
  display: string;
  value: number | string | null;
  editable: boolean;
  numeric: boolean;
  /** Set on a time column so an edit can name the period. */
  period?: string;
  indicator?: string;
  coordinates?: Record<string, string>;
}

export interface TaskGridRow {
  id: string;
  cells: TaskGridCell[];
}

interface TaskGridProperties {
  label: string;
  columnTitles: string[];
  rows: TaskGridRow[];
  disabled?: boolean;
  freezeColumns?: number;
  onEdit: (rowIndex: number, columnIndex: number, value: number | string | null) => void;
}

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 36;

const TaskGrid = ({
  label,
  columnTitles,
  rows,
  disabled = false,
  freezeColumns = 1,
  onEdit,
}: TaskGridProperties) => {
  const columns = useMemo<GridColumn[]>(
    () =>
      columnTitles.map((title, index) => ({
        id: String(index),
        title,
        width: index === 0 ? 180 : 120,
      })),
    [columnTitles],
  );

  const theme = useMemo(
    () => ({
      accentColor: accent.solid,
      accentFg: text.inverse,
      accentLight: accent.subtle,
      textDark: text.primary,
      textMedium: text.secondary,
      textLight: text.muted,
      textHeader: text.primary,
      textHeaderSelected: text.inverse,
      bgCell: neutral.surface,
      bgCellMedium: neutral.surfaceMuted,
      bgHeader: neutral.surfaceMuted,
      bgHeaderHovered: neutral.surfaceHover,
      bgHeaderHasFocus: accent.subtle,
      borderColor: neutral.border,
      horizontalBorderColor: neutral.border,
      bgBubble: accent.subtle,
      bgBubbleSelected: accent.subtle,
      fontFamily: font.body,
      baseFontStyle: '13px',
      headerFontStyle: '600 12px',
      editorFontSize: '13px',
    }),
    [],
  );

  const muted = useMemo(() => ({ bgCell: neutral.surfaceMuted, textDark: text.secondary }), []);
  const editableTheme = useMemo(() => ({ bgCell: accent.subtle }), []);

  const height = Math.min(440, HEADER_HEIGHT + Math.max(rows.length, 1) * ROW_HEIGHT + 8);
  const gridWidth = columns.reduce((total, column) => {
    const width = 'width' in column && typeof column.width === 'number' ? column.width : 120;
    return total + width;
  }, 0);

  if (rows.length === 0) {
    return (
      <Text fontSize='13px' color={text.secondary}>
        Nothing to show on this grid yet.
      </Text>
    );
  }

  const getCellContent = ([column, row]: Item): GridCell => {
    const cell = rows[row]?.cells[column];
    if (!cell) {
      return {
        kind: GridCellKind.Text,
        data: '',
        displayData: '',
        allowOverlay: false,
        readonly: true,
      };
    }
    const canEdit = !disabled && cell.editable;
    const base = {
      allowOverlay: canEdit,
      readonly: !canEdit,
      themeOverride: canEdit ? editableTheme : muted,
    };
    if (cell.numeric && typeof cell.value === 'number') {
      return {
        ...base,
        kind: GridCellKind.Number,
        data: cell.value,
        displayData: cell.display,
        allowNegative: true,
      };
    }
    return {
      ...base,
      kind: GridCellKind.Text,
      data: cell.value == null ? '' : String(cell.value),
      displayData: cell.display,
    };
  };

  return (
    <Box>
      <Text fontSize='12px' fontWeight={600} color={text.secondary} mb={2}>
        {label}
        <Text as='span' fontWeight={400} ml={2}>
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </Text>
      </Text>
      <Box
        h={`${height}px`}
        w='100%'
        border={`1px solid ${neutral.border}`}
        borderRadius={radius.surface}
        overflow='auto'
        bg={neutral.surface}
      >
        <DataEditor
          width={Math.max(gridWidth, 320)}
          height={height}
          columns={columns}
          rows={rows.length}
          theme={theme}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          getCellContent={getCellContent}
          freezeColumns={freezeColumns}
          smoothScrollX
          smoothScrollY
          onCellsEdited={(edits) => {
            edits.forEach(({ location, value }) => {
              const [column, row] = location;
              if (value.kind === GridCellKind.Number) onEdit(row, column, value.data ?? null);
              else if (value.kind === GridCellKind.Text) onEdit(row, column, value.data);
            });
            return true;
          }}
        />
      </Box>
    </Box>
  );
};

export default TaskGrid;
